/**
 * Centralized environment variable access
 * This provides a clean abstraction over Vite's import.meta.env
 * and ensures type safety for all environment variables
 */

function resolveShowDevtools(): boolean {
  const override = import.meta.env.VITE_SHOW_DEVTOOLS;
  if (override === 'true') return true;
  if (override === 'false') return false;
  return import.meta.env.DEV;
}
const showDevtools = resolveShowDevtools();

export const env = {
  /**
   * Backend API endpoint URL
   * @default 'http://localhost:3001'
   */
  BACKEND_ENDPOINT: (import.meta.env.VITE_BACKEND_ENDPOINT || 'http://localhost:3001').replace(
    /\/$/,
    ''
  ),

  /**
   * Enable/disable MSW (Mock Service Worker) for API mocking
   * @default false
   */
  USE_MOCK: import.meta.env.VITE_USE_MOCK === 'true',

  /**
   * Feature flag: Enable Viral Recording V2
   * @default false
   */
  USE_VIRAL_RECORDING_V2: import.meta.env.VITE_USE_VIRAL_RECORDING_V2 === 'true',

  /**
   * Feature flag: show the "Sources" chip row under assistant answers.
   * @default false
   */
  USE_SOURCES_UI: import.meta.env.VITE_USE_SOURCES_UI === 'true',

  /**
   * Show TanStack Query/Router devtools.
   * Unset (or empty) → follows DEV (on in dev, off in prod). Only the exact strings
   * 'true'/'false' override, so devtools can be silenced in dev or enabled against a
   * prod build. Consumers lazy-load the devtools packages behind this flag, so a prod
   * build keeps them in a separate chunk that is never fetched unless the flag is on.
   */
  SHOW_DEVTOOLS: showDevtools,

  /**
   * Current environment mode (development, production, test)
   */
  MODE: import.meta.env.MODE,

  /**
   * Is development mode
   */
  DEV: import.meta.env.DEV,

  /**
   * Is production mode
   */
  PROD: import.meta.env.PROD
} as const;

/**
 * Type-safe environment variable access
 */
export type Env = typeof env;
